import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnInit,
  SimpleChanges,
  ChangeDetectionStrategy
} from "@angular/core";
import { Router } from "@angular/router";
import { ApiService } from "../../../services/api.service";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { AnimationCollapse, AnimationCollapseLeave } from "../../../animations";
import { Sort, MatSortModule } from "@angular/material/sort";
import { DeviceExclusionReason, IDeviceStatus, getExclusionReasonKey } from "../../../models/devicestatus.model";
import { HelperService } from "../../../services/helper.service";
import { Angular2Csv } from "angular2-csv";
import moment from "moment-timezone";
import { MomentPipe } from "../../../pipes/pipes";
import { DeviceDatabaseComponent } from "../../device-database/device-database.component";
import { NgClass } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { DeviceService } from "app/services/device.service";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { RemoveExclusionDialogComponent } from "./remove-exclusion-dialog.component";
import { AddExclusionDialogComponent } from "./add-exclusion-dialog.component";
import { Areas } from "app/models/permission.model";
import { PermissionService } from "app/services/permission.service";

@Component({
    selector: "devicestatus",
    templateUrl: "./devicestatus.component.html",
    styleUrls: ["./devicestatus.component.css"],
    animations: [AnimationCollapse, AnimationCollapseLeave],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
    FormsModule,
    NgClass,
    DeviceDatabaseComponent,
    MatSortModule,
    MatDialogModule,
    TranslateModule,
    MomentPipe
]
})
export class DeviceStatusComponent implements OnChanges, OnInit {
  @Input("type") statusType: string;
  @Input("class") statusClass: string;
  @Input("data") countData: number;
  @Input("title") statusTitle: string;
  @Input() showBaseDevicesOnly: boolean;
  @Input() description: string;
  @Input() useCompanyGroup: boolean = false;
  @Input() disableRowClick: boolean = false;
  @Input() showCustomer: boolean = false;
  /** Allow excluding devices (off on the scoped landing page dashboard) */
  @Input() allowExclusion: boolean = true;
  @Output() onDeviceRemoved = new EventEmitter<void>();
  @Output() onDeviceExcluded = new EventEmitter<void>();
  baseDeviceList: IDeviceStatus[];

  constructor(
    private api: ApiService,
    private router: Router,
    private translate: TranslateService,
    private helper: HelperService,
    private service: DeviceService,
    private dialog: MatDialog,
    private permissions: PermissionService
  ) {}

  onlineStatus: OnlineStatus = {
    state: false,
    deviceList: null,
  };

  originalDevList: IDeviceStatus[];
  user: any;

  getBaseDevices(): IDeviceStatus[] {
    return this.originalDevList.filter((deviceStatus) =>
      ["31", "40", "36"].some((prefix) => deviceStatus.id.startsWith(prefix))
    );
  }

  selectDevicesToShow(): void {
    this.onlineStatus.deviceList = this.showBaseDevicesOnly
      ? this.baseDeviceList
      : this.originalDevList;
  }

  showProductStatus: boolean = false;

  displayOnlineStatusDevices(): void {
    if (this.statusType == "productStatus") {
      /*
      let customerId = JSON.parse(localStorage.getItem("user"))["customerId"];
      console.log(customerId);
      this.api.getCompanyInventory(customerId).subscribe((response: any) => {

      });
      */
      this.showProductStatus = true;
    } else if (!this.onlineStatus.state) {
      const devices$ = this.useCompanyGroup
        ? this.api.getOnlineStatusCompanyGroupDevices(this.statusType)
        : this.api.getOnlineStatusDevices(this.statusType);
      devices$.subscribe((response: any) => {
          this.onlineStatus.deviceList = response;
          this.originalDevList = response.slice();
          this.baseDeviceList = this.getBaseDevices();
          this.selectDevicesToShow();
        });
    } else {
      this.onlineStatus.deviceList = null;
    }
    this.onlineStatus.state = !this.onlineStatus.state;
  }

  openDevice(dev: any): void {
    this.router.navigate(["device/" + dev.id]);
  }

  removeExclusion(event: Event, dev: IDeviceStatus): void {
    if (!this.canExclude) return;
    event.stopPropagation();
    const dialogRef = this.dialog.open(RemoveExclusionDialogComponent, {
      data: { deviceId: dev.id, deviceName: dev.name },
      maxWidth: '480px'
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.api.deleteDeviceExclusion(dev.id).subscribe(() => {
        this.originalDevList = this.originalDevList.filter(d => d.id !== dev.id);
        this.baseDeviceList = this.baseDeviceList?.filter(d => d.id !== dev.id);
        this.selectDevicesToShow();
        this.onDeviceRemoved.emit();
      });
    });
  }

  get canExclude(): boolean {
    return this.allowExclusion && this.permissions.hasOrTierLevel(Areas.Devices, "ReadWrite", 95, this.user?.roleLevel);
  }

  addExclusion(event: Event, dev: IDeviceStatus): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(AddExclusionDialogComponent, {
      data: { deviceId: dev.id, deviceName: dev.name },
      width: '480px'
    });
    dialogRef.afterClosed().subscribe((reason: number | null) => {
      if (reason === null || reason === undefined) return;
      this.api.setDeviceExclusion(dev.id, reason).subscribe(() => {
        this.originalDevList = this.originalDevList.filter(d => d.id !== dev.id);
        this.baseDeviceList = this.baseDeviceList?.filter(d => d.id !== dev.id);
        this.selectDevicesToShow();
        this.onDeviceExcluded.emit();
      });
    });
  }

  getDeviceType(type: string): string {
    return this.helper.getDeviceType(type);
  }

  sortData(sort: Sort) {
    if (!sort.active || sort.direction == "") {
      // this.onlineStatus.deviceList = this.originalDevList;
      return;
    }

    this.onlineStatus.deviceList.sort((a, b) => {
      const isAsc = sort.direction == "asc";
      switch (sort.active) {
        case "id":
          return this.compare(a.id, b.id, isAsc);
        case "deviceType":
          return this.compare(a.deviceType, b.deviceType, isAsc);
        case "name":
          return this.compare(a.name, b.name, isAsc);
        case "lastcon":
          return this.compare(a.lastConnection, b.lastConnection, isAsc);
        case "location":
          return this.compare(a.locationName, b.locationName, isAsc);
        case "softwareVersion":
          return this.compare(a.softwareVersion, b.softwareVersion, isAsc);
        case "exclusionReason":
          return this.compare(a.exclusionReason, b.exclusionReason, isAsc);
        default:
          return 0;
      }
    });
  }

  compare(a, b, isAsc) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  getExclusionReason(reason: DeviceExclusionReason): string {
    return getExclusionReasonKey(reason);
  }

  exportAsCSV(): void {
    const csvHeaders = [
      "ID",
      "Name",
    ];

    if (this.showCustomer) {
      csvHeaders.push("Client");
    }

    csvHeaders.push(...[
      "Location",
      "Address",
      "Postal code",
      "City",
      "Type",
      "Software version",
    ]);

    // Additional info: Devices ReadWrite for customer users, level >= Editor for tiers (as the API)
    if (this.permissions.hasOrTierLevel(Areas.Devices, "ReadWrite", 50, this.user?.roleLevel)) {
      csvHeaders.push(...["Additional information", "Device information"]);
    }

    if (this.statusType === 'Excluded') {
      csvHeaders.push("Exclusion reason");
    }

    // Push rest of the headers separately to mantain correct order
    csvHeaders.push(...["Latest contact registered", "Battery status"]);

    const csvData = [];
    const filename = "deviceStatus-" + moment().format("YYYY-MM-DD");
    const options = {
      fieldSeparator: ";",
      quoteStrings: '"',
      decimalseparator: ".",
      showTitle: false,
      headers: csvHeaders,
    };
    //Define what information is included in the file
    this.onlineStatus.deviceList.forEach((row) => {
      const csvRow: any = {};
      csvRow.DeviceId = `="${row.id}"`;
      csvRow.Name = row.name || "ID-" + row.id;
      if (this.showCustomer) {
        csvRow.Client = row.customerName;
      }
      csvRow.Location = row.locationName;
      csvRow.Address = row.address;
      csvRow.PostCode = row.postCode;
      csvRow.City = row.postOffice;
      csvRow.DeviceType = this.getDeviceType(row.deviceType);
      csvRow.Firmware = row.softwareVersion;
      if (this.permissions.hasOrTierLevel(Areas.Devices, "ReadWrite", 50, this.user?.roleLevel)) {
        csvRow.AdditionalInfo = row.additionalInfo;
        csvRow.DeviceInformation = row.additionalAlarmInfo;
      }
      if (this.statusType === 'Excluded') {
        csvRow.ExclusionReason = this.translate.instant(this.getExclusionReason(row.exclusionReason));
      }
      csvRow.lastContact = moment(row.lastConnection).format("l LTS");
      csvRow.batteryState = row.batteryState;
      csvData.push(csvRow);
    });
    //Generate the file and send it to the browser
    new Angular2Csv(csvData, filename, options);
  }

  async getDeviceTranslations(): Promise<void> {
    let devTypeList = localStorage.getItem("devType");
    //If list is not in storage download it from the server and set it to local storage
    if (!devTypeList) {
      await new Promise<void>((resolve) =>
        this.api
          .getDeviceTranslations(localStorage.getItem("language"))
          .subscribe((res) => {
            localStorage.setItem("devType", JSON.stringify(res));
            devTypeList = res;
            resolve();
          })
      );
    } else {
      await Promise.resolve();
    }
  }

  ngOnInit() {
    this.user = JSON.parse(localStorage.getItem("user"))
      ?? JSON.parse(localStorage.getItem("landing-page"))?.user;
    this.getDeviceTranslations();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.selectDevicesToShow();
  }
}

class OnlineStatus {
  state: boolean;
  deviceList: IDeviceStatus[];
}
